import { Children, cloneElement, type ReactElement, type ReactNode } from 'react';

import type { ButtonProps as KitButtonProps, ButtonSize, ButtonVariant } from 'elowen-plugin-ui-kit';
import { cn } from '../../lib/utils';
import { Button as ShadcnButton, buttonVariants } from './shadcn/button';

/** The app-shaped button, composed from the shadcn/ui `Button` in `./shadcn/button.tsx`.
 *
 *  Only the vocabulary is this file's: the app names its variants by emphasis (`accent` is the brand
 *  fill, `default` is the quiet one) where shadcn names them by role (`default` IS the brand). The two
 *  disagree on the meaning of the word `default`, so they cannot be merged into one prop type without
 *  making 39 call sites ambiguous — this map is what keeps every one of them untouched. It is temporary:
 *  a later phase moves the call sites onto the shadcn names and deletes the map with this wrapper.
 *
 *  It is also the ONLY app→shadcn variant map: `IconButton` used to keep a second, silently divergent one
 *  in which `default` meant `outline` rather than `secondary`. `outline` and `ghost-danger` are what that
 *  wrapper needs for its two variants, expressed in this vocabulary, and `outline-danger` is what a
 *  labelled destructive control asks for, so no file outside this one spells a shadcn variant. */
/** The vocabulary and the size steps are the KIT's (`elowen-plugin-ui-kit`), because a bundle spends the
 *  same names on the same component: restating them here is how a plugin came to name a variant the app
 *  does not have. Re-exported under this file's own names for the host callers that already import them. */
export type { ButtonSize, ButtonVariant };

/** The app→shadcn name map. The `satisfies` clause is the native validator: a kit name without a shadcn
 *  counterpart — or the reverse — fails to compile here, so the two vocabularies cannot drift. */
const SHADCN_VARIANT = {
  default: 'secondary',
  accent: 'default',
  ghost: 'ghost',
  danger: 'destructive',
  'ghost-danger': 'ghost-destructive',
  outline: 'outline',
  'outline-danger': 'outline-destructive',
} as const satisfies Record<ButtonVariant, NonNullable<Parameters<typeof buttonVariants>[0]>['variant']>;

/** The button's classes without the button — for the handful of places that need the same control on an
 *  `<a>` or a `<label>`. Callers append their own overrides through `className`, and going through `cn()`
 *  means an override actually WINS: the old template-literal version emitted both `h-9` and the caller's
 *  `h-10` and left the winner to stylesheet order. `size` is a real parameter rather than one more thing
 *  to hand-write into `className`, so that a link-shaped button picks the same geometry a real one would. */
export function buttonClassName(variant: ButtonVariant = 'default', size: ButtonSize = 'default', className = ''): string {
  return cn(buttonVariants({ variant: SHADCN_VARIANT[variant], size }), className);
}

/** The kit's published `ButtonProps` plus the one capability only the host has: `asChild`, which hands the
 *  caller's own element the button's classes. A plugin does not get it — a plugin surface links through the
 *  host's navigation — so it is not part of the published shape. */
export type ButtonProps = KitButtonProps & {
  /** Render the caller's own element — a link, a label — with this button's classes and NO button of ours.
   *  Radix's `Slot` merges onto exactly one child and refuses everything else, so the caller passes one. */
  asChild?: boolean;
};

export function Button({ variant = 'default', size = 'default', icon: Icon, className = '', children, asChild = false, ...rest }: ButtonProps) {
  const glyph = Icon ? <Icon size={14} aria-hidden /> : null;
  // Slot takes ONE child and throws `React.Children.only` for anything else, so `asChild` cannot hand it the
  // glyph and the label as two children — which is what a dashboard action link hit: a button used as a link
  // crashed instead of rendering. The glyph goes INSIDE the caller's element, ahead of its own content,
  // which is the same place it takes in the `<button>` form. `Children.only` stays the guard for a caller
  // that passes something that is not exactly one element.
  if (asChild) {
    const child = Children.only(children) as ReactElement<{ children?: ReactNode }>;
    return (
      <ShadcnButton variant={SHADCN_VARIANT[variant]} size={size} className={className} asChild {...rest}>
        {Icon ? cloneElement(child, child.props, <>{glyph}{child.props.children}</>) : child}
      </ShadcnButton>
    );
  }
  return (
    <ShadcnButton variant={SHADCN_VARIANT[variant]} size={size} className={className} {...rest}>
      {glyph}
      {children}
    </ShadcnButton>
  );
}
